import { loadGsapPlugins } from '@theme/gsap'
import { CenterCardAutoplay } from '@theme/infinite-collection-autoplay'

const SLIDE_DURATION = 1.5
const THROW_MAX = 1
const THROW_MIN = 0.5
const DRAG_RES = 0.025
const THROW_RES = 2000
const CLICK_EASE = 'expo.out'
const RESIZE_DELAY = 200

const ROOT = '[data-infinite-collection]'
const VIEWPORT = '[data-infinite-collection-viewport]'
const TRACK = '[data-infinite-collection-track]'
const ITEM = '[data-infinite-collection-item]'
const CONTROL = '[data-infinite-collection-control]'
const STATUS = 'data-infinite-collection-item-status'
const DRAG_STATUS = 'data-flick-drag-status'
const PAGE_CHANGED_EVENT = 'page:changed'

const mod = (n, m) => ((n % m) + m) % m

async function initInfiniteCollection() {
  const gsap = await loadGsapPlugins(['Draggable', 'InertiaPlugin'])
  const { Draggable } = window
  if (!(gsap && Draggable)) return

  for (const root of document.querySelectorAll(ROOT)) {
    initSlider(root, gsap, Draggable)
  }
}

function initSlider(root, gsap, Draggable) {
  root._sliderDraggable?.kill()
  root.setAttribute(DRAG_STATUS, 'grab')

  const collection = root.querySelector(VIEWPORT)
  const track = root.querySelector(TRACK)
  const items = Array.from(root.querySelectorAll(ITEM))
  const controls = Array.from(root.querySelectorAll(CONTROL))
  if (!(collection && track && items.length)) return

  const styles = getComputedStyle(root)
  const rotate_step =
    Number.parseFloat(
      styles.getPropertyValue('--infinite-collection-rotate')
    ) || 0
  let spv_var = Number.parseFloat(
    styles.getPropertyValue('--infinite-collection-spv')
  )

  const first_rect = items[0].getBoundingClientRect()
  const item_w = first_rect.width
  const item_h = first_rect.height

  if (Number.isNaN(spv_var)) spv_var = collection.clientWidth / item_w

  const spv = Math.max(1, Math.min(spv_var, items.length))
  const vis_count = Math.ceil(spv)
  const is_draggable = items.length > 1 && rotate_step > 0 && item_w > 0
  const is_renderable = rotate_step > 0 && item_w > 0

  if (!is_renderable) {
    return
  }

  const n = items.length
  const autoplay = getAutoplay(root)
  const itemStatuses = new Array(n).fill('')
  const nextStatuses = new Array(n).fill('not-active')
  let activeIndex = -1

  track.style.height = `${item_h}px`
  gsap.set(items, { xPercent: -50 })

  const set_rot = items.map((el) => gsap.quickSetter(el, 'rotate', 'deg'))

  const proxy = document.createElement('div')
  gsap.set(proxy, { x: 0 })

  const idx_from_proxy = () => -gsap.getProperty(proxy, 'x') / item_w

  const nearest_delta = (i, idx_real, len) => {
    const k = Math.round((idx_real - i) / len)
    return i - (idx_real - k * len)
  }

  function set_statuses(idx_real) {
    const idx_mod = mod(Math.round(idx_real), n)
    const left = Math.floor((vis_count - 1) / 2)
    const right = vis_count - 1 - left

    nextStatuses.fill('not-active')
    nextStatuses[idx_mod] = 'active'

    for (let k = 1; k <= right; k++) {
      nextStatuses[mod(idx_mod + k, n)] = 'inview'
    }
    for (let k = 1; k <= left; k++) {
      nextStatuses[mod(idx_mod - k, n)] = 'inview'
    }

    for (let i = 0; i < n; i++) {
      if (itemStatuses[i] === nextStatuses[i]) continue
      itemStatuses[i] = nextStatuses[i]
      items[i].setAttribute(STATUS, nextStatuses[i])
    }

    if (activeIndex !== idx_mod) {
      activeIndex = idx_mod
      autoplay?.syncActive(idx_mod, items)
    }
  }

  function render() {
    const idx_real = idx_from_proxy()
    for (let i = 0; i < n; i++) {
      const rotation = nearest_delta(i, idx_real, n) * rotate_step
      set_rot[i](rotation)
    }
    set_statuses(idx_real)
  }

  const tweenTo = (target_idx) => {
    gsap.killTweensOf(proxy)
    gsap.to(proxy, {
      x: -target_idx * item_w,
      duration: SLIDE_DURATION,
      ease: CLICK_EASE,
      onUpdate: render
    })
  }

  for (const btn of controls) {
    const val = btn.getAttribute('data-infinite-collection-control')
    if (!(val === 'next' || val === 'prev')) {
      continue
    }

    btn.disabled = false
    btn.onclick = () => {
      tweenTo(Math.round(idx_from_proxy()) + (val === 'next' ? 1 : -1))
    }
  }

  if (is_draggable) {
    root._sliderDraggable = Draggable.create(proxy, {
      type: 'x',
      trigger: collection,
      inertia: true,
      cursor: 'inherit',
      activeCursor: 'inherit',
      maxDuration: THROW_MAX,
      minDuration: THROW_MIN,
      dragResistance: DRAG_RES,
      throwResistance: THROW_RES,
      bounds: null,
      edgeResistance: 0,
      snap: (v) => Math.round(v / item_w) * item_w,
      onDrag: render,
      onThrowUpdate: render,
      onPress: () => root.setAttribute(DRAG_STATUS, 'grabbing'),
      onDragStart: () => root.setAttribute(DRAG_STATUS, 'grabbing'),
      onRelease: () => root.setAttribute(DRAG_STATUS, 'grab'),
      onThrowComplete: () => {
        root.setAttribute(DRAG_STATUS, 'grab')
        render()
      }
    })[0]
  }

  gsap.set(track, { x: 0 })
  render()
}

function getAutoplay(root) {
  if (root._sliderAutoplay) return root._sliderAutoplay

  const autoplay = new CenterCardAutoplay(root)
  autoplay.observe()
  root._sliderAutoplay = autoplay
  return autoplay
}

let resizeTimer
let lastWidth = window.innerWidth

window.addEventListener('resize', () => {
  if (window.innerWidth === lastWidth) return
  lastWidth = window.innerWidth
  clearTimeout(resizeTimer)
  resizeTimer = setTimeout(initInfiniteCollection, RESIZE_DELAY)
})

document.addEventListener('shopify:section:load', initInfiniteCollection)
window.addEventListener(PAGE_CHANGED_EVENT, initInfiniteCollection)

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfiniteCollection)
} else {
  initInfiniteCollection()
}
